import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, OpenInNew } from "@mui/icons-material";

interface Props {
  /** Presigned S3 URL — fetched server-side via the /view endpoint
   * (which also emits the audit event). */
  url: string;
  contentType: string;
  fileName: string;
  versionLabel: string;
  onClose: () => void;
}

/** In-drawer viewer. PDFs render via the browser's native PDF viewer
 * inside an iframe. DOCX files are converted to HTML client-side using
 * mammoth so we don't have to ship a server-side renderer or send the
 * file out to a third-party viewer (privacy). */
export function DocumentVersionViewer({
  url,
  contentType,
  fileName,
  versionLabel,
  onClose,
}: Props) {
  const isPdf = contentType === "application/pdf";
  const isDocx =
    contentType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    /\.docx$/i.test(fileName);

  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [docxLoading, setDocxLoading] = useState(false);
  const [docxError, setDocxError] = useState<string | null>(null);

  useEffect(() => {
    if (!isDocx) return;
    let cancelled = false;
    setDocxLoading(true);
    setDocxError(null);
    setDocxHtml(null);
    (async () => {
      try {
        // Fetch the file via the presigned URL, then convert in-browser.
        // Mammoth is lazy-imported so the chunk only loads when a DOCX
        // is actually being viewed.
        const [{ default: mammoth }, blob] = await Promise.all([
          import("mammoth"),
          fetch(url).then((r) => {
            if (!r.ok) throw new Error(`Fetch failed: ${r.status}`);
            return r.arrayBuffer();
          }),
        ]);
        if (cancelled) return;
        const result = await mammoth.convertToHtml({ arrayBuffer: blob });
        if (cancelled) return;
        setDocxHtml(result.value);
      } catch (e) {
        if (cancelled) return;
        setDocxError(e instanceof Error ? e.message : "Could not render document");
      } finally {
        if (!cancelled) setDocxLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, isDocx]);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 0.75,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Viewing {versionLabel} · {fileName}
        </Typography>
        <Box>
          <Tooltip title="Open in new tab">
            <IconButton
              size="small"
              component="a"
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close viewer">
            <IconButton size="small" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ height: 560 }}>
        {isPdf && (
          <iframe
            title={`PDF viewer · ${fileName}`}
            src={url}
            style={{ width: "100%", height: "100%", border: 0 }}
          />
        )}
        {isDocx && (
          <Box sx={{ height: "100%", overflowY: "auto", p: 2 }}>
            {docxLoading && (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {docxError && (
              <Alert severity="error">{docxError}</Alert>
            )}
            {docxHtml && (
              <Box
                sx={{
                  "& p": { mb: 1 },
                  "& h1,h2,h3": { mt: 2, mb: 1 },
                  "& table": { borderCollapse: "collapse", my: 1 },
                  "& td, & th": { border: "1px solid", borderColor: "divider", p: 1 },
                }}
                /* mammoth output is HTML it constructed from the .docx
                   — safe to inject; no user-supplied HTML pathway. */
                dangerouslySetInnerHTML={{ __html: docxHtml }}
              />
            )}
          </Box>
        )}
        {!isPdf && !isDocx && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">
              In-browser preview isn't available for this file type. Use the
              download icon next to the version row, or open in a new tab.
            </Alert>
          </Box>
        )}
      </Box>
    </Box>
  );
}
